import { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import BackButton from '../../components/feature/BackButton';
import PhoneInput from '../../components/feature/PhoneInput';
import { supabase } from '../../lib/supabase';

interface PatientSimilaire {
  id: string;
  nom: string;
  prenom: string;
  date_naissance: string;
  email?: string;
  telephone?: string;
}

export default function RegisterPage() {
  const [userType, setUserType] = useState<'patient' | 'medecin' | null>(null);
  const [loginMethod, setLoginMethod] = useState<'email' | 'phone'>('email');
  const [formData, setFormData] = useState({
    email: '',
    phone: '',
    password: '',
    confirmPassword: '',
    nom: '',
    prenom: '',
    dateNaissance: '',
    specialite: '',
    token: '',
    sexe: '',
  });
  const [consentAccepted, setConsentAccepted] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showDoublonPopup, setShowDoublonPopup] = useState(false);
  const [patientSimilaire, setPatientSimilaire] = useState<PatientSimilaire | null>(null);
  const [checkingDoublon, setCheckingDoublon] = useState(false);
  
  // États pour l'OTP téléphone
  const [otpStep, setOtpStep] = useState<'form' | 'otp' | 'email-verification' | 'complete' | 'google-complete'>('form');
  const [otpCode, setOtpCode] = useState('');
  const [otpSent, setOtpSent] = useState(false);
  const [resendTimer, setResendTimer] = useState(0);
  
  // États pour la vérification email
  const [emailVerificationCode, setEmailVerificationCode] = useState('');
  const [emailCodeSent, setEmailCodeSent] = useState(false);
  const [emailResendTimer, setEmailResendTimer] = useState(0);
  
  // État pour Cloudflare Turnstile
  const [turnstileToken, setTurnstileToken] = useState('');
  const [turnstileReady, setTurnstileReady] = useState(false);
  const [turnstileError, setTurnstileError] = useState(false);
  const [turnstileKey, setTurnstileKey] = useState(0);
  const turnstileContainerRef = useRef<HTMLDivElement>(null);
  const turnstileWidgetIdRef = useRef<string | null>(null);
  const turnstileMountedRef = useRef(false);
  
  const { signUp, user, role, loading: authLoading, signInWithGoogle } = useAuth();
  const navigate = useNavigate();

  // Gérer le retour de l'OAuth Google pour l'inscription
  useEffect(() => {
    if (!authLoading && user) {
      const googleAction = sessionStorage.getItem('google_auth_action');
      if (googleAction === 'signup') {
        sessionStorage.removeItem('google_auth_action');
        
        if (role) {
          // L'utilisateur a déjà un profil, on le déconnecte et on le redirige vers la connexion
          console.log('[Register] Google signup - compte déjà existant, rôle:', role, '- redirection vers login');
          supabase.auth.signOut().then(() => {
            navigate('/login', { 
              replace: true,
              state: { 
                infoMessage: 'Un compte existe déjà avec cette adresse email. Veuillez vous connecter avec votre mot de passe ou avec Google.' 
              } 
            });
          });
        } else {
          // Nouvel utilisateur Google - afficher le formulaire de complétion
          console.log('[Register] Google signup - nouveau patient, affichage formulaire');
          setOtpStep('google-complete');
        }
      }
    }
  }, [user, role, authLoading, navigate]);

  // Charger Cloudflare Turnstile
  useEffect(() => {
    // Ne charger Turnstile que quand on est sur le formulaire
    if (otpStep !== 'form' || !userType) return;

    let cancelled = false;
    let timeoutId: ReturnType<typeof setTimeout>;

    const renderTurnstile = () => {
      if (cancelled || !turnstileContainerRef.current) return;
      // Éviter le double render (StrictMode / remount)
      if (turnstileMountedRef.current) return;

      try {
        const widgetId = (window.turnstile as any).render(turnstileContainerRef.current, {
          sitekey: '0x4AAAAAACFuWJkMdzmwzN09',
          callback: (token: string) => {
            if (!cancelled) {
              console.log('✅ Turnstile token reçu (register)');
              setTurnstileToken(token);
            }
          },
          'expired-callback': () => {
            if (!cancelled) {
              console.log('⚠️ Turnstile token expiré (register)');
              setTurnstileToken('');
            }
          },
          'error-callback': () => {
            if (!cancelled) {
              console.log('❌ Erreur Turnstile (register)');
              setTurnstileToken('');
              setTurnstileError(true);
            }
          },
        });
        turnstileWidgetIdRef.current = widgetId || null;
        turnstileMountedRef.current = true;
        if (!cancelled) {
          setTurnstileReady(true);
          setTurnstileError(false);
        }
      } catch (err) {
        console.error('❌ Exception Turnstile render (register):', err);
        if (!cancelled) {
          setTurnstileError(true);
        }
      }
    };

    // Essayer de charger Turnstile immédiatement, ou attendre le script
    if (window.turnstile) {
      // Rendu immédiat — le script est déjà en cache, le DOM est prêt
      renderTurnstile();
      return () => {
        cancelled = true;
        if (turnstileWidgetIdRef.current && window.turnstile) {
          try { (window.turnstile as any).remove(turnstileWidgetIdRef.current); } catch (e) { /* ignore */ }
          turnstileWidgetIdRef.current = null;
        }
        turnstileMountedRef.current = false;
      };
    }

    // Script pas encore chargé → polling avec timeout
    const checkInterval = setInterval(() => {
      if (cancelled) {
        clearInterval(checkInterval);
        return;
      }
      if (window.turnstile) {
        clearInterval(checkInterval);
        clearTimeout(timeoutId);
        renderTurnstile();
      }
    }, 200);

    // Timeout de sécurité : si le script ne charge pas en 12 secondes, afficher erreur
    timeoutId = setTimeout(() => {
      clearInterval(checkInterval);
      if (!cancelled && !turnstileMountedRef.current) {
        console.error('❌ Turnstile script non chargé après 12s (register)');
        setTurnstileError(true);
      }
    }, 12000);

    return () => {
      cancelled = true;
      clearInterval(checkInterval);
      clearTimeout(timeoutId);
      if (turnstileWidgetIdRef.current && window.turnstile) {
        try { (window.turnstile as any).remove(turnstileWidgetIdRef.current); } catch (e) { /* ignore */ }
        turnstileWidgetIdRef.current = null;
      }
      turnstileMountedRef.current = false;
    };
  }, [otpStep, userType, turnstileKey]);

  const handleGoogleSignUp = async () => {
    setError('');
    setLoading(true);

    try {
      sessionStorage.setItem('google_auth_action', 'signup');
      await signInWithGoogle(`${window.location.origin}/register`);
    } catch (err: any) {
      console.error('❌ Erreur inscription Google:', err);
      setError('Erreur lors de l\'inscription avec Google');
      setLoading(false);
    }
  };

  // Vérifier les doublons potentiels
  const checkForDoublons = async () => {
    if (!formData.nom || !formData.prenom || !formData.dateNaissance) {
      return null;
    }

    setCheckingDoublon(true);
    try {
      // Vérifier par nom, prénom et date de naissance
      const { data: byIdentity, error: identityError } = await supabase
        .from('patients')
        .select('id, nom, prenom, date_naissance, email, telephone')
        .ilike('nom', formData.nom)
        .ilike('prenom', formData.prenom)
        .eq('date_naissance', formData.dateNaissance)
        .maybeSingle();

      if (byIdentity) {
        return byIdentity;
      }

      // Vérifier par email si fourni
      if (formData.email) {
        const { data: byEmail, error: emailError } = await supabase
          .from('patients')
          .select('id, nom, prenom, date_naissance, email, telephone')
          .ilike('email', formData.email)
          .maybeSingle();

        if (byEmail) {
          return byEmail;
        }
      }

      // Vérifier par téléphone si fourni
      if (formData.phone) {
        const { data: byPhone, error: phoneError } = await supabase
          .from('patients')
          .select('id, nom, prenom, date_naissance, email, telephone')
          .eq('telephone', formData.phone)
          .maybeSingle();

        if (byPhone) {
          return byPhone;
        }
      }

      return null;
    } catch (error) {
      console.error('Erreur lors de la vérification des doublons:', error);
      return null;
    } finally {
      setCheckingDoublon(false);
    }
  };

  // Envoyer le code de vérification par email
  const sendEmailVerificationCode = async () => {
    setError('');
    setLoading(true);

    try {
      const emailToVerify = userType === 'medecin' ? formData.email : (loginMethod === 'email' ? formData.email : formData.email);
      
      if (!emailToVerify) {
        throw new Error('Email requis pour la vérification');
      }

      const response = await fetch(`${import.meta.env.VITE_PUBLIC_SUPABASE_URL}/functions/v1/send-verification-code`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          identifier: emailToVerify,
          action: 'register',
          type: 'email'
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Erreur lors de l\'envoi du code');
      }

      setEmailCodeSent(true);
      setOtpStep('email-verification');
      
      // Démarrer le timer de 60 secondes
      setEmailResendTimer(60);
      const interval = setInterval(() => {
        setEmailResendTimer((prev) => {
          if (prev <= 1) {
            clearInterval(interval);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);

    } catch (err: any) {
      setError(err.message || 'Erreur lors de l\'envoi du code');
    } finally {
      setLoading(false);
    }
  };

  // Vérifier le code email
  const verifyEmailCode = async () => {
    setError('');
    setLoading(true);

    try {
      const emailToVerify = userType === 'medecin' ? formData.email : (loginMethod === 'email' ? formData.email : formData.email);

      const response = await fetch(`${import.meta.env.VITE_PUBLIC_SUPABASE_URL}/functions/v1/verify-email-code`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          identifier: emailToVerify,
          code: emailVerificationCode,
          action: 'register'
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Code invalide');
      }

      // Code vérifié, procéder à l'inscription
      await proceedWithRegistration();

    } catch (err: any) {
      setError(err.message || 'Code invalide ou expiré');
      setLoading(false);
    }
  };

  // Envoyer le code OTP par SMS
  const sendOtpCode = async () => {
    setError('');
    setLoading(true);

    try {
      // Validation : vérifier que le numéro est complet
      const digitsOnly = formData.phone.replace(/\D/g, '');
      if (digitsOnly.length < 8) {
        throw new Error('Numéro de téléphone incomplet. Veuillez saisir un numéro valide.');
      }

      const response = await fetch(`${import.meta.env.VITE_PUBLIC_SUPABASE_URL}/functions/v1/send-phone-otp`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          phone: formData.phone,
          action: 'register',
          turnstileToken
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        // Détecter le code d'erreur spécial pour crédits SMS épuisés
        if (data.error && data.error.includes('SMS_INACTIVE|')) {
          const userMessage = data.error.split('SMS_INACTIVE|')[1];
          throw new Error(userMessage);
        }
        throw new Error(data.error || 'Erreur lors de l\'envoi du code');
      }

      setOtpSent(true);
      setOtpStep('otp');
      
      // Démarrer le timer de 60 secondes
      setResendTimer(60);
      const interval = setInterval(() => {
        setResendTimer((prev) => {
          if (prev <= 1) {
            clearInterval(interval);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);

    } catch (err: any) {
      setError(err.message || 'Erreur lors de l\'envoi du code');
    } finally {
      setLoading(false);
    }
  };

  // Vérifier le code OTP
  const verifyOtpCode = async () => {
    setError('');
    setLoading(true);

    try {
      const response = await fetch(`${import.meta.env.VITE_PUBLIC_SUPABASE_URL}/functions/v1/verify-phone-otp`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          phone: formData.phone,
          code: otpCode
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Code invalide');
      }

      // Code vérifié, procéder à l'inscription
      await proceedWithRegistration();

    } catch (err: any) {
      setError(err.message || 'Code invalide ou expiré');
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (formData.password !== formData.confirmPassword) {
      setError('Les mots de passe ne correspondent pas');
      return;
    }

    if (formData.password.length < 10) {
      setError('Le mot de passe doit contenir au moins 10 caractères');
      return;
    }

    if (!consentAccepted) {
      setError('Vous devez accepter les conditions pour créer un compte');
      return;
    }

    // Vérifier Cloudflare Turnstile
    if (!turnstileToken) {
      setError('Veuillez compléter la vérification anti-robot');
      return;
    }

    if (userType === 'patient' && loginMethod === 'phone' && !formData.phone.trim()) {
      setError('Le numéro de téléphone est requis');
      return;
    }

    if (userType === 'patient' && loginMethod === 'email' && !formData.email.trim()) {
      setError('L\'email est requis');
      return;
    }

    if (userType === 'medecin' && !formData.token.trim()) {
      setError('Le token d\'inscription est requis pour créer un compte médecin');
      return;
    }

    // Vérifier les doublons pour les patients
    if (userType === 'patient') {
      const doublon = await checkForDoublons();
      if (doublon) {
        setPatientSimilaire(doublon);
        setShowDoublonPopup(true);
        return;
      }
    }

    // Si c'est une inscription par téléphone, envoyer le code OTP
    if (userType === 'patient' && loginMethod === 'phone') {
      await sendOtpCode();
    } else {
      // Pour email ou médecin, envoyer le code de vérification email
      await sendEmailVerificationCode();
    }
  };

  const proceedWithRegistration = async () => {
    setLoading(true);

    try {
      await signUp(
        loginMethod === 'email' ? formData.email : formData.phone,
        formData.password,
        userType!,
        loginMethod,
        {
          nom: formData.nom,
          prenom: formData.prenom,
          dateNaissance: userType === 'patient' ? formData.dateNaissance : undefined,
          specialite: userType === 'medecin' ? formData.specialite : undefined,
          sexe: formData.sexe || null,
        },
        userType === 'medecin' ? formData.token : undefined
      );

      if (userType === 'medecin') {
        navigate('/medecin/rendez-vous', { replace: true });
      } else {
        navigate('/rendez-vous', { replace: true });
      }
    } catch (err: any) {
      setError(err.message || 'Une erreur est survenue lors de l\'inscription');
    } finally {
      setLoading(false);
    }
  };

  // Finaliser l'inscription Google : créer le dossier patient
  const completeGoogleRegistration = async () => {
    setError('');
    setLoading(true);

    if (!formData.nom.trim() || !formData.prenom.trim() || !formData.dateNaissance) {
      setError('Veuillez remplir tous les champs obligatoires');
      setLoading(false);
      return;
    }

    if (!consentAccepted) {
      setError('Vous devez accepter les conditions pour créer un compte');
      setLoading(false);
      return;
    }

    try {
      // Récupérer l'email Google
      const { data: authData } = await supabase.auth.getUser();
      const googleEmail = authData.user?.email || '';
      const userId = authData.user?.id;

      if (!userId) {
        throw new Error('Session Google invalide');
      }

      console.log('[Register] Création patient Google:', { nom: formData.nom, prenom: formData.prenom, email: googleEmail });

      // Insérer le patient directement
      const { error: insertError } = await supabase
        .from('patients')
        .insert({
          user_id: userId,
          nom: formData.nom,
          prenom: formData.prenom,
          date_naissance: formData.dateNaissance,
          email: googleEmail,
          created_by_staff: false,
          must_change_password: false,
          privacy_consent_accepted: true,
          privacy_consent_date: new Date().toISOString(),
        });

      if (insertError) {
        console.error('[Register] Erreur insertion patient:', insertError);
        throw new Error('Erreur lors de la création du dossier patient');
      }

      console.log('[Register] Patient Google créé avec succès !');
      navigate('/patient/rendez-vous', { replace: true });
    } catch (err: any) {
      console.error('[Register] Erreur complétion Google:', err);
      setError(err.message || 'Une erreur est survenue');
    } finally {
      setLoading(false);
    }
  };

  const handleDoublonResponse = (isCorrectPerson: boolean) => {
    if (isCorrectPerson) {
      // Rediriger vers la page de connexion
      setShowDoublonPopup(false);
      navigate('/login', { 
        state: { 
          message: 'Un compte existe déjà avec ces informations. Veuillez vous connecter.' 
        } 
      });
    } else {
      // Permettre la création du compte
      setShowDoublonPopup(false);
      setPatientSimilaire(null);
      
      // Si c'est une inscription par téléphone, envoyer le code OTP
      if (userType === 'patient' && loginMethod === 'phone') {
        sendOtpCode();
      } else {
        proceedWithRegistration();
      }
    }
  };

  // Page de choix du type d'utilisateur
  if (!userType) {
    return (
      <div className="min-h-screen bg-white flex flex-col">
        <Header />

        <div className="flex-1 flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
          <div className="max-w-md w-full">
            <div className="mb-6">
              <BackButton />
            </div>
            <div className="bg-white p-8 rounded-3xl shadow-xl border-2 border-clinic-blue/10">
              <div className="text-center mb-8">
                <div className="w-16 h-16 bg-clinic-blue/10 rounded-2xl flex items-center justify-center mx-auto mb-4">
                  <i className="ri-user-add-line text-3xl text-clinic-blue"></i>
                </div>
                <h2 className="text-3xl font-bold text-clinic-blue-dark">Créer un compte</h2>
                <p className="text-gray-600 mt-2">Choisissez votre type de compte</p>
              </div>

              <div className="space-y-4">
                <button
                  onClick={() => navigate('/creer-compte-patient')}
                  className="w-full p-6 border-2 border-clinic-blue/20 rounded-2xl hover:border-clinic-blue hover:bg-clinic-blue/5 transition-all text-left cursor-pointer"
                >
                  <div className="flex items-center">
                    <div className="w-12 h-12 bg-clinic-green/10 rounded-2xl flex items-center justify-center mr-4">
                      <i className="ri-user-heart-line text-2xl text-clinic-green"></i>
                    </div>
                    <div>
                      <h3 className="font-semibold text-clinic-blue-dark mb-1">Patient</h3>
                      <p className="text-gray-600 text-sm">Prendre des rendez-vous médicaux</p>
                    </div>
                  </div>
                </button>

                <button
                  onClick={() => setUserType('medecin')}
                  className="w-full p-6 border-2 border-clinic-blue/20 rounded-2xl hover:border-clinic-blue hover:bg-clinic-blue/5 transition-all text-left cursor-pointer"
                >
                  <div className="flex items-center">
                    <div className="w-12 h-12 bg-clinic-blue/10 rounded-2xl flex items-center justify-center mr-4">
                      <i className="ri-stethoscope-line text-2xl text-clinic-blue"></i>
                    </div>
                    <div>
                      <h3 className="font-semibold text-clinic-blue-dark mb-1">Médecin</h3>
                      <p className="text-gray-600 text-sm">Gérer mes consultations et patients</p>
                    </div>
                  </div>
                </button>
              </div>

              <div className="mt-8 text-center">
                <p className="text-gray-600">
                  Déjà un compte ?{' '}
                  <Link to="/login" className="text-clinic-blue hover:text-clinic-blue-dark font-semibold transition-colors">
                    Connectez-vous
                  </Link>
                </p>
                <p className="mt-3 text-sm">
                  <Link to="/creer-compte-patient" className="text-clinic-green hover:text-clinic-green/80 font-semibold transition-colors whitespace-nowrap">
                    <i className="ri-key-2-line mr-1"></i>
                    Code d'activation reçu à la clinique ?
                  </Link>
                </p>
              </div>
            </div>
          </div>
        </div>

        <Footer />
      </div>
    );
  }

  // Page de vérification OTP téléphone
  if (otpStep === 'otp') {
    return (
      <div className="min-h-screen bg-white flex flex-col">
        <Header />

        <div className="flex-1 flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
          <div className="max-w-md w-full">
            <div className="mb-6">
              <button
                onClick={() => setOtpStep('form')}
                className="text-gray-500 hover:text-gray-700 cursor-pointer flex items-center gap-2"
              >
                <i className="ri-arrow-left-line text-xl"></i>
                <span>Retour</span>
              </button>
            </div>
            <div className="bg-white p-8 rounded-3xl shadow-xl border-2 border-clinic-blue/10">
              <div className="text-center mb-8">
                <div className="w-16 h-16 bg-clinic-blue/10 rounded-2xl flex items-center justify-center mx-auto mb-4">
                  <i className="ri-smartphone-line text-3xl text-clinic-blue"></i>
                </div>
                <h2 className="text-3xl font-bold text-clinic-blue-dark">Vérification</h2>
                <p className="text-gray-600 mt-2">
                  Un code à 6 chiffres a été envoyé au
                </p>
                <p className="text-clinic-blue font-semibold mt-1">{formData.phone}</p>
              </div>

              {error && (
                <div className="mb-4 p-4 bg-red-50 border-2 border-red-200 text-red-600 rounded-2xl text-sm">
                  {error}
                </div>
              )}

              <div className="space-y-6">
                <div>
                  <label htmlFor="otp" className="block text-sm font-medium text-clinic-blue-dark mb-2">
                    Code de vérification
                  </label>
                  <input
                    id="otp"
                    type="text"
                    maxLength={6}
                    value={otpCode}
                    onChange={(e) => setOtpCode(e.target.value.replace(/\D/g, ''))}
                    className="w-full px-4 py-3 border-2 border-clinic-blue/20 rounded-2xl focus:outline-none focus:border-clinic-blue text-center text-2xl font-mono tracking-widest"
                    placeholder="000000"
                  />
                </div>

                <button
                  onClick={verifyOtpCode}
                  disabled={loading || otpCode.length !== 6}
                  className="w-full px-6 py-3 bg-clinic-blue-dark text-white rounded-full hover:bg-clinic-blue transition-all font-semibold shadow-lg hover:shadow-xl transform hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer"
                >
                  {loading ? 'Vérification...' : 'Vérifier le code'}
                </button>

                <div className="text-center">
                  {resendTimer > 0 ? (
                    <p className="text-gray-600 text-sm">
                      Renvoyer le code dans {resendTimer}s
                    </p>
                  ) : (
                    <button
                      onClick={sendOtpCode}
                      disabled={loading}
                      className="text-clinic-blue hover:text-clinic-blue-dark font-semibold text-sm cursor-pointer"
                    >
                      Renvoyer le code
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>

        <Footer />
      </div>
    );
  }

  // Page de vérification email
  if (otpStep === 'email-verification') {
    return (
      <div className="min-h-screen bg-white flex flex-col">
        <Header />

        <div className="flex-1 flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
          <div className="max-w-md w-full">
            <div className="mb-6">
              <button
                onClick={() => setOtpStep('form')}
                className="text-gray-500 hover:text-gray-700 cursor-pointer flex items-center gap-2"
              >
                <i className="ri-arrow-left-line text-xl"></i>
                <span>Retour</span>
              </button>
            </div>
            <div className="bg-white p-8 rounded-3xl shadow-xl border-2 border-clinic-blue/10">
              <div className="text-center mb-8">
                <div className="w-16 h-16 bg-clinic-blue/10 rounded-2xl flex items-center justify-center mx-auto mb-4">
                  <i className="ri-mail-line text-3xl text-clinic-blue"></i>
                </div>
                <h2 className="text-3xl font-bold text-clinic-blue-dark">Vérification Email</h2>
                <p className="text-gray-600 mt-2">
                  Un code à 6 chiffres a été envoyé à
                </p>
                <p className="text-clinic-blue font-semibold mt-1">
                  {userType === 'medecin' ? formData.email : (loginMethod === 'email' ? formData.email : formData.email)}
                </p>
              </div>

              {error && (
                <div className="mb-4 p-4 bg-red-50 border-2 border-red-200 text-red-600 rounded-2xl text-sm">
                  {error}
                </div>
              )}

              <div className="space-y-6">
                <div>
                  <label htmlFor="emailCode" className="block text-sm font-medium text-clinic-blue-dark mb-2">
                    Code de vérification
                  </label>
                  <input
                    id="emailCode"
                    type="text"
                    maxLength={6}
                    value={emailVerificationCode}
                    onChange={(e) => setEmailVerificationCode(e.target.value.replace(/\D/g, ''))}
                    className="w-full px-4 py-3 border-2 border-clinic-blue/20 rounded-2xl focus:outline-none focus:border-clinic-blue text-center text-2xl font-mono tracking-widest"
                    placeholder="000000"
                  />
                </div>

                <button
                  onClick={verifyEmailCode}
                  disabled={loading || emailVerificationCode.length !== 6}
                  className="w-full px-6 py-3 bg-clinic-blue-dark text-white rounded-full hover:bg-clinic-blue transition-all font-semibold shadow-lg hover:shadow-xl transform hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer"
                >
                  {loading ? 'Vérification...' : 'Vérifier le code'}
                </button>

                <div className="text-center">
                  {emailResendTimer > 0 ? (
                    <p className="text-gray-600 text-sm">
                      Renvoyer le code dans {emailResendTimer}s
                    </p>
                  ) : (
                    <button
                      onClick={sendEmailVerificationCode}
                      disabled={loading}
                      className="text-clinic-blue hover:text-clinic-blue-dark font-semibold text-sm cursor-pointer"
                    >
                      Renvoyer le code
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>

        <Footer />
      </div>
    );
  }

  // Page de complétion après inscription Google
  if (otpStep === 'google-complete') {
    return (
      <div className="min-h-screen bg-white flex flex-col">
        <Header />

        <div className="flex-1 flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
          <div className="max-w-md w-full">
            <div className="mb-6">
              <button
                onClick={() => {
                  setOtpStep('form');
                  supabase.auth.signOut();
                }}
                className="text-gray-500 hover:text-gray-700 cursor-pointer flex items-center gap-2"
              >
                <i className="ri-arrow-left-line text-xl"></i>
                <span>Annuler</span>
              </button>
            </div>
            <div className="bg-white p-8 rounded-3xl shadow-xl border-2 border-clinic-blue/10">
              <div className="text-center mb-8">
                <div className="w-16 h-16 bg-clinic-blue/10 rounded-2xl flex items-center justify-center mx-auto mb-4">
                  <svg className="w-8 h-8" viewBox="0 0 24 24">
                    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                    <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                    <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                  </svg>
                </div>
                <h2 className="text-3xl font-bold text-clinic-blue-dark">Finaliser l'inscription</h2>
                <p className="text-gray-600 mt-2">
                  Votre compte Google est connecté. Complétez vos informations pour créer votre dossier patient.
                </p>
              </div>

              {error && (
                <div className="mb-4 p-4 bg-red-50 border-2 border-red-200 text-red-600 rounded-2xl text-sm">
                  {error}
                </div>
              )}

              <div className="space-y-6">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label htmlFor="google-nom" className="block text-sm font-medium text-clinic-blue-dark mb-2">
                      Nom
                    </label>
                    <input
                      id="google-nom"
                      type="text"
                      required
                      value={formData.nom}
                      onChange={(e) => setFormData({ ...formData, nom: e.target.value })}
                      className="w-full px-4 py-3 border-2 border-clinic-blue/20 rounded-2xl focus:outline-none focus:border-clinic-blue text-sm"
                      placeholder="Votre nom"
                    />
                  </div>
                  <div>
                    <label htmlFor="google-prenom" className="block text-sm font-medium text-clinic-blue-dark mb-2">
                      Prénom
                    </label>
                    <input
                      id="google-prenom"
                      type="text"
                      required
                      value={formData.prenom}
                      onChange={(e) => setFormData({ ...formData, prenom: e.target.value })}
                      className="w-full px-4 py-3 border-2 border-clinic-blue/20 rounded-2xl focus:outline-none focus:border-clinic-blue text-sm"
                      placeholder="Votre prénom"
                    />
                  </div>
                </div>

                <div>
                  <label htmlFor="google-dn" className="block text-sm font-medium text-clinic-blue-dark mb-2">
                    Date de naissance
                  </label>
                  <input
                    id="google-dn"
                    type="date"
                    required
                    value={formData.dateNaissance}
                    onChange={(e) => setFormData({ ...formData, dateNaissance: e.target.value })}
                    className="w-full px-4 py-3 border-2 border-clinic-blue/20 rounded-2xl focus:outline-none focus:border-clinic-blue text-sm"
                  />
                </div>

                {/* Consentement */}
                <div className="space-y-3">
                  <div className="flex items-start">
                    <input
                      type="checkbox"
                      id="google-consent"
                      checked={consentAccepted}
                      onChange={(e) => setConsentAccepted(e.target.checked)}
                      className="mt-1 h-4 w-4 text-clinic-blue focus:ring-clinic-blue border-gray-300 rounded cursor-pointer"
                    />
                    <label htmlFor="google-consent" className="ml-3 text-sm text-gray-700">
                      J'accepte que mes informations médicales soient utilisées dans le cadre de mes soins et conformément à la{' '}
                      <a href="/politique-confidentialite" target="_blank" className="text-clinic-blue hover:underline font-semibold">
                        Politique de Confidentialité
                      </a>
                    </label>
                  </div>
                  <p className="text-xs text-gray-500 ml-7">
                    <i className="ri-shield-check-line mr-1"></i>
                    Vos données sont protégées conformément à la loi n°2017-20 du Bénin
                  </p>
                </div>

                <button
                  onClick={completeGoogleRegistration}
                  disabled={loading || !consentAccepted}
                  className="w-full px-6 py-3 bg-clinic-blue-dark text-white rounded-full hover:bg-clinic-blue transition-all font-semibold shadow-lg hover:shadow-xl transform hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer"
                >
                  {loading ? 'Création du compte...' : 'Créer mon compte patient'}
                </button>
              </div>
            </div>
          </div>
        </div>

        <Footer />
      </div>
    );
  }

  // Page de formulaire d'inscription
  return (
    <div className="min-h-screen bg-white flex flex-col">
      <Header />

      <div className="flex-1 flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-md w-full">
          <div className="mb-6">
            <BackButton />
          </div>
          <div className="bg-white p-8 rounded-3xl shadow-xl border-2 border-clinic-blue/10">
            <div className="text-center mb-8">
              <button
                onClick={() => setUserType(null)}
                className="absolute top-4 left-4 text-gray-500 hover:text-gray-700 cursor-pointer"
              >
                <i className="ri-arrow-left-line text-xl"></i>
              </button>
              <div className="w-16 h-16 bg-clinic-blue/10 rounded-2xl flex items-center justify-center mx-auto mb-4">
                <i className={`${userType === 'medecin' ? 'ri-stethoscope-line' : 'ri-user-heart-line'} text-3xl text-clinic-blue`}></i>
              </div>
              <h2 className="text-3xl font-bold text-clinic-blue-dark">
                Inscription {userType === 'medecin' ? 'Médecin' : 'Patient'}
              </h2>
              <p className="text-gray-600 mt-2">
                {userType === 'medecin' 
                  ? 'Créez votre compte professionnel avec votre token d\'inscription' 
                  : 'Créez votre compte patient'}
              </p>
            </div>

            {checkingDoublon && (
              <div className="mb-4 p-4 bg-blue-50 border-2 border-blue-200 rounded-2xl flex items-center gap-3">
                <i className="ri-loader-4-line text-xl text-blue-600 animate-spin"></i>
                <span className="text-blue-800 font-medium">Vérification des doublons...</span>
              </div>
            )}

            {error && (
              <div className="mb-4 p-4 bg-red-50 border-2 border-red-200 text-red-600 rounded-2xl text-sm">
                {error}
              </div>
            )}

            {/* Bouton d'inscription Google - UNIQUEMENT pour les patients */}
            {userType === 'patient' && (
              <>
                <button
                  type="button"
                  onClick={handleGoogleSignUp}
                  disabled={loading}
                  className="w-full mb-6 px-6 py-3 bg-white border-2 border-gray-300 text-gray-700 rounded-full hover:bg-gray-50 hover:border-gray-400 transition-all font-semibold shadow-md hover:shadow-lg flex items-center justify-center gap-3 disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
                >
                  <svg className="w-5 h-5" viewBox="0 0 24 24">
                    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                    <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                    <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                  </svg>
                  S'inscrire avec Google
                </button>

                {/* Séparateur */}
                <div className="relative mb-6">
                  <div className="absolute inset-0 flex items-center">
                    <div className="w-full border-t border-gray-300"></div>
                  </div>
                  <div className="relative flex justify-center text-sm">
                    <span className="px-4 bg-white text-gray-500">Ou</span>
                  </div>
                </div>
              </>
            )}

            <form onSubmit={handleSubmit} className="space-y-6">
              {userType === 'medecin' && (
                <div className="p-4 bg-yellow-50 border-2 border-yellow-200 rounded-2xl">
                  <div className="flex items-start">
                    <i className="ri-key-line text-yellow-600 text-xl mr-2 mt-0.5"></i>
                    <div className="text-sm">
                      <p className="font-semibold text-yellow-800 mb-1">Token d'inscription requis</p>
                      <p className="text-yellow-700">Contactez l'administration pour obtenir votre token d'inscription médecin</p>
                    </div>
                  </div>
                  <div className="mt-3">
                    <input
                      type="text"
                      placeholder="Saisissez votre token d'inscription"
                      required
                      value={formData.token}
                      onChange={(e) => setFormData({ ...formData, token: e.target.value.toUpperCase() })}
                      className="w-full px-4 py-3 border-2 border-yellow-300 rounded-2xl focus:outline-none focus:border-yellow-500 text-sm font-mono"
                    />
                  </div>
                </div>
              )}

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label htmlFor="nom" className="block text-sm font-medium text-clinic-blue-dark mb-2">
                    Nom
                  </label>
                  <input
                    id="nom"
                    type="text"
                    required
                    value={formData.nom}
                    onChange={(e) => setFormData({ ...formData, nom: e.target.value })}
                    className="w-full px-4 py-3 border-2 border-clinic-blue/20 rounded-2xl focus:outline-none focus:border-clinic-blue text-sm"
                  />
                </div>

                <div>
                  <label htmlFor="prenom" className="block text-sm font-medium text-clinic-blue-dark mb-2">
                    Prénom
                  </label>
                  <input
                    id="prenom"
                    type="text"
                    required
                    value={formData.prenom}
                    onChange={(e) => setFormData({ ...formData, prenom: e.target.value })}
                    className="w-full px-4 py-3 border-2 border-clinic-blue/20 rounded-2xl focus:outline-none focus:border-clinic-blue text-sm"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-clinic-blue-dark mb-2">Sexe</label>
                <select
                  value={formData.sexe}
                  onChange={(e) => setFormData({ ...formData, sexe: e.target.value })}
                  className="w-full px-4 py-3 border-2 border-clinic-blue/20 rounded-2xl focus:outline-none focus:border-clinic-blue text-sm pr-8"
                >
                  <option value="">Sélectionner</option>
                  <option value="Masculin">Masculin</option>
                  <option value="Féminin">Féminin</option>
                </select>
              </div>

              {userType === 'patient' && (
                <>
                  <div>
                    <label className="block text-sm font-medium text-clinic-blue-dark mb-3">
                      Méthode de connexion
                    </label>
                    <div className="flex gap-3">
                      <button
                        type="button"
                        onClick={() => setLoginMethod('email')}
                        className={`flex-1 px-4 py-3 rounded-2xl border-2 transition-all cursor-pointer ${
                          loginMethod === 'email'
                            ? 'bg-clinic-blue text-white border-clinic-blue'
                            : 'bg-white text-gray-700 border-clinic-blue/20 hover:border-clinic-blue/40'
                        }`}
                      >
                        <i className="ri-mail-line mr-2"></i>
                        Email
                      </button>
                      <button
                        type="button"
                        onClick={() => setLoginMethod('phone')}
                        className={`flex-1 px-4 py-3 rounded-2xl border-2 transition-all cursor-pointer ${
                          loginMethod === 'phone'
                            ? 'bg-clinic-blue text-white border-clinic-blue'
                            : 'bg-white text-gray-700 border-clinic-blue/20 hover:border-clinic-blue/40'
                        }`}
                      >
                        <i className="ri-phone-line mr-2"></i>
                        Téléphone
                      </button>
                    </div>
                  </div>

                  <div>
                    <label htmlFor="dateNaissance" className="block text-sm font-medium text-clinic-blue-dark mb-2">
                      Date de naissance
                    </label>
                    <input
                      id="dateNaissance"
                      type="date"
                      required
                      value={formData.dateNaissance}
                      onChange={(e) => setFormData({ ...formData, dateNaissance: e.target.value })}
                      className="w-full px-4 py-3 border-2 border-clinic-blue/20 rounded-2xl focus:outline-none focus:border-clinic-blue text-sm"
                    />
                  </div>
                </>
              )}

              {(userType === 'medecin' || loginMethod === 'email') && (
                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-clinic-blue-dark mb-2">
                    Email
                  </label>
                  <input
                    id="email"
                    type="email"
                    required
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    className="w-full px-4 py-3 border-2 border-clinic-blue/20 rounded-2xl focus:outline-none focus:border-clinic-blue text-sm"
                    placeholder="votre@email.com"
                  />
                </div>
              )}

              {userType === 'patient' && loginMethod === 'phone' && (
                <PhoneInput
                  id="phone"
                  name="phone"
                  label="Numéro de téléphone"
                  required
                  value={formData.phone}
                  onChange={(phone) => setFormData({ ...formData, phone })}
                />
              )}

              <div>
                <label htmlFor="password" className="block text-sm font-medium text-clinic-blue-dark mb-2">
                  Mot de passe
                </label>
                <input
                  id="password"
                  type="password"
                  required
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  className="w-full px-4 py-3 border-2 border-clinic-blue/20 rounded-2xl focus:outline-none focus:border-clinic-blue text-sm"
                  placeholder="Minimum 10 caractères"
                />
              </div>

              <div>
                <label htmlFor="confirmPassword" className="block text-sm font-medium text-clinic-blue-dark mb-2">
                  Confirmer le mot de passe
                </label>
                <input
                  id="confirmPassword"
                  type="password"
                  required
                  value={formData.confirmPassword}
                  onChange={(e) => setFormData({ ...formData, confirmPassword: e.target.value })}
                  className="w-full px-4 py-3 border-2 border-clinic-blue/20 rounded-2xl focus:outline-none focus:border-clinic-blue text-sm"
                />
              </div>

              {/* Cloudflare Turnstile - conteneur TOUJOURS rendu pour éviter le deadlock */}
              <div className="flex justify-center min-h-[65px] items-center relative">
                {/* Le container Turnstile - toujours monté, invisible pendant le chargement */}
                <div
                  key={turnstileKey}
                  ref={turnstileContainerRef}
                  className={turnstileReady ? '' : 'opacity-0 absolute pointer-events-none'}
                ></div>

                {/* Spinner pendant le chargement */}
                {!turnstileReady && !turnstileError && (
                  <div className="flex items-center gap-2 text-gray-400">
                    <i className="ri-loader-4-line animate-spin"></i>
                    <span className="text-sm">Chargement de la vérification...</span>
                  </div>
                )}

                {/* Message d'erreur */}
                {turnstileError && (
                  <div className="text-center py-2">
                    <p className="text-red-500 text-sm mb-2 flex items-center justify-center gap-1">
                      <i className="ri-error-warning-line"></i>
                      La vérification anti-robot n'a pas pu être chargée
                    </p>
                    <button
                      type="button"
                      onClick={() => {
                        setTurnstileError(false);
                        setTurnstileReady(false);
                        setTurnstileToken('');
                        turnstileMountedRef.current = false;
                        turnstileWidgetIdRef.current = null;
                        setTurnstileKey(prev => prev + 1);
                      }}
                      className="text-sm text-clinic-blue hover:text-clinic-blue-dark font-semibold underline cursor-pointer whitespace-nowrap"
                    >
                      <i className="ri-refresh-line mr-1"></i>
                      Réessayer
                    </button>
                  </div>
                )}
              </div>

              {/* Consentement pour les patients */}
              {userType === 'patient' && (
                <div className="space-y-3">
                  <div className="flex items-start">
                    <input
                      type="checkbox"
                      id="privacy-consent"
                      checked={consentAccepted}
                      onChange={(e) => setConsentAccepted(e.target.checked)}
                      className="mt-1 h-4 w-4 text-clinic-blue focus:ring-clinic-blue border-gray-300 rounded cursor-pointer"
                    />
                    <label htmlFor="privacy-consent" className="ml-3 text-sm text-gray-700">
                      J'accepte que mes informations médicales soient utilisées dans le cadre de mes soins et conformément à la{' '}
                      <a href="/politique-confidentialite" target="_blank" className="text-clinic-blue hover:underline font-semibold">
                        Politique de Confidentialité
                      </a>
                    </label>
                  </div>
                  <p className="text-xs text-gray-500 ml-7">
                    <i className="ri-shield-check-line mr-1"></i>
                    Vos données sont protégées conformément à la loi n°2017-20 du Bénin et déclarées auprès de l'APDP
                  </p>
                </div>
              )}

              {/* Consentement pour les médecins */}
              {userType === 'medecin' && (
                <div className="space-y-3">
                  <div className="flex items-start">
                    <input
                      type="checkbox"
                      id="ethics-consent"
                      checked={consentAccepted}
                      onChange={(e) => setConsentAccepted(e.target.checked)}
                      className="mt-1 h-4 w-4 text-clinic-blue focus:ring-clinic-blue border-gray-300 rounded cursor-pointer"
                    />
                    <label htmlFor="ethics-consent" className="ml-3 text-sm text-gray-700">
                      J'accepte de respecter la{' '}
                      <a href="/charte-deontologie" target="_blank" className="text-clinic-blue hover:underline font-semibold">
                        Charte de Déontologie Médicale
                      </a>
                      {' '}et je m'engage à protéger le secret médical et les données des patients conformément à la{' '}
                      <a href="/politique-confidentialite" target="_blank" className="text-clinic-blue hover:underline font-semibold">
                        Politique de Confidentialité
                      </a>
                    </label>
                  </div>
                  <p className="text-xs text-gray-500 ml-7">
                    <i className="ri-stethoscope-line mr-1"></i>
                    En tant que professionnel de santé, vous êtes soumis au secret médical et aux obligations déontologiques
                  </p>
                </div>
              )}

              <button
                type="submit"
                disabled={loading || checkingDoublon || !turnstileToken}
                className="w-full px-6 py-3 bg-clinic-blue-dark text-white rounded-full hover:bg-clinic-blue transition-all font-semibold shadow-lg hover:shadow-xl transform hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer"
              >
                {loading ? 'Création du compte...' : `Créer mon compte ${userType === 'medecin' ? 'médecin' : 'patient'}`}
              </button>
            </form>

            <div className="mt-6 text-center">
              <p className="text-gray-600">
                Déjà un compte ?{' '}
                <Link to="/login" className="text-clinic-blue hover:text-clinic-blue-dark font-semibold transition-colors">
                  Connectez-vous
                </Link>
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Popup de confirmation de doublon */}
      {showDoublonPopup && patientSimilaire && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-3xl shadow-2xl max-w-md w-full p-8">
            <div className="text-center mb-6">
              <div className="w-16 h-16 bg-yellow-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
                <i className="ri-alert-line text-3xl text-yellow-600"></i>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-2">Compte existant détecté</h3>
              <p className="text-gray-600">Un compte avec des informations similaires a été trouvé</p>
            </div>

            <div className="bg-gray-50 p-6 rounded-2xl mb-6 space-y-3">
              <div>
                <p className="text-sm text-gray-600">Nom complet</p>
                <p className="font-semibold text-gray-900">{patientSimilaire.nom} {patientSimilaire.prenom}</p>
              </div>
              <div>
                <p className="text-sm text-gray-600">Date de naissance</p>
                <p className="font-semibold text-gray-900">
                  {new Date(patientSimilaire.date_naissance + 'T00:00:00').toLocaleDateString('fr-FR')}
                </p>
              </div>
              {patientSimilaire.email && (
                <div>
                  <p className="text-sm text-gray-600">Email</p>
                  <p className="font-semibold text-gray-900">{patientSimilaire.email}</p>
                </div>
              )}
              {patientSimilaire.telephone && (
                <div>
                  <p className="text-sm text-gray-600">Téléphone</p>
                  <p className="font-semibold text-gray-900">{patientSimilaire.telephone}</p>
                </div>
              )}
            </div>

            <div className="mb-6 p-4 bg-blue-50 border-2 border-blue-200 rounded-2xl">
              <p className="text-sm text-blue-800 text-center">
                <i className="ri-question-line mr-2"></i>
                Est-ce votre compte ?
              </p>
            </div>

            <div className="space-y-3">
              <button
                onClick={() => handleDoublonResponse(true)}
                className="w-full px-6 py-4 bg-clinic-blue text-white rounded-full hover:bg-clinic-blue-dark transition-all font-semibold whitespace-nowrap cursor-pointer"
              >
                <i className="ri-check-line mr-2"></i>
                Oui, c'est mon compte
              </button>
              <button
                onClick={() => handleDoublonResponse(false)}
                className="w-full px-6 py-4 border-2 border-gray-300 text-gray-700 rounded-full hover:bg-gray-50 transition-all font-semibold whitespace-nowrap cursor-pointer"
              >
                <i className="ri-close-line mr-2"></i>
                Non, ce n'est pas moi
              </button>
            </div>
          </div>
        </div>
      )}

      <Footer />
    </div>
  );
}
